"use client";

import { Fragment, useEffect, useRef, useState } from "react";
import { AdCard, useAds } from "@/components/ads/Ads";
import type { Page, VideoSummary } from "@/lib/api/types";
import { type FeedParams, InfiniteFeed } from "./InfiniteFeed";
import { VideoCard } from "./VideoCard";
import { useUi } from "@/components/ui/use-ui";

/** Narrowest card in the "Up next" column, and the grid gaps (gap-x-3.5, gap-y-6). */
const CARD_MIN = 300;
const GAP_X = 14;
const GAP_Y = 24;
/** Shown before the column has been measured (one column beside a 16:9 player). */
const DEFAULT_UP_NEXT = 4;

/**
 * The watch page. From xl up, the player is sized to fit the screen's height, which leaves room
 * beside it: an "Up next" column of related videos fills that room with as many whole cards as fit
 * next to the player and its details. Below, the related feed continues across the full width,
 * without the cards already shown beside the player.
 */
export function WatchLayout({ main, related, params }: { main: React.ReactNode; related: Page<VideoSummary>; params: FeedParams }) {
  const mainRef = useRef<HTMLDivElement>(null);
  const asideRef = useRef<HTMLElement>(null);
  const [upNext, setUpNext] = useState(Math.min(DEFAULT_UP_NEXT, related.items.length));
  // The ad network's card banner takes the second place of "Up next" (it is a card's size).
  const ad = !!useAds().ads?.card;
  const shown = ad && upNext > 1 ? upNext - 1 : upNext;
  const t = useUi();

  useEffect(() => {
    const column = mainRef.current;
    const aside = asideRef.current;
    if (!column || !aside) return;
    const measure = () => {
      // Hidden below xl: keep the count, it only applies from xl up.
      if (getComputedStyle(aside).display === "none") return;
      const width = aside.clientWidth;
      const cols = Math.max(1, Math.floor((width + GAP_X) / (CARD_MIN + GAP_X)));
      const cardWidth = (width - (cols - 1) * GAP_X) / cols;
      // A card is a 16:9 thumbnail plus a caption whose height doesn't depend on the width.
      const card = aside.querySelector<HTMLElement>("[data-up-next] > *");
      const caption = card && card.offsetWidth ? card.offsetHeight - card.offsetWidth * (9 / 16) : 76;
      const cardHeight = cardWidth * (9 / 16) + caption;
      const heading = aside.querySelector<HTMLElement>("h2");
      const room = column.offsetHeight - (heading ? heading.offsetHeight + 12 : 0);
      const rows = Math.max(1, Math.floor((room + GAP_Y) / (cardHeight + GAP_Y)));
      setUpNext(Math.min(related.items.length, rows * cols));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(column);
    observer.observe(aside);
    return () => observer.disconnect();
  }, [related.items.length]);

  return (
    <>
      <div className="xl:flex xl:items-start xl:gap-5">
        <div ref={mainRef} className="min-w-0 xl:flex-[0_1_calc((100svh-190px)*16/9)]">
          {main}
        </div>
        {related.items.length > 0 && (
          <aside ref={asideRef} aria-label={t.video.upNext} className="hidden min-w-0 xl:block xl:min-w-[300px] xl:flex-1">
            <h2 className="mb-3 text-[15px] text-fg">{t.video.upNext}</h2>
            <div data-up-next className="grid gap-x-3.5 gap-y-6 [grid-template-columns:repeat(auto-fill,minmax(300px,1fr))]">
              {related.items.slice(0, shown).map((v, i) => (
                <Fragment key={v.id}>
                  {ad && i === 1 && <AdCard />}
                  <VideoCard video={v} eager={i < 3} />
                </Fragment>
              ))}
            </div>
          </aside>
        )}
      </div>

      <section className="mt-8" aria-label={t.video.moreVideos}>
        <InfiniteFeed initial={related} params={params} hideFirstOnWide={shown} ads />
      </section>
    </>
  );
}
